import {Directive, DOCUMENT, effect, EffectRef, ElementRef, inject, input, InputSignal, OnDestroy, OnInit} from '@angular/core';
import {TooltipPositionEnum} from '../../enums/tooltip/tooltip-position.enum';
import {TooltipStyleEnum} from '../../enums/tooltip/tooltip-style.enum';
import {defaultContextmenuDelay} from '../../constants/ui/default-contextmenu-delay.constant';

@Directive({
    selector: '[appTooltip]',
    host: {
        '(pointerenter)': 'onPointerEnter($event)',
        '(pointerleave)': 'onPointerLeave($event)',
        '(focus)': 'showTooltip()',
        '(blur)': 'hideTooltip()',
        '(click)': 'hideTooltip()',
        '(pointerdown)': 'onPointerDown($event)',
        '(pointerup)': 'onPointerUpOrCancel()',
        '(pointercancel)': 'onPointerUpOrCancel()',
    },
})
export class TooltipDirective implements OnInit, OnDestroy {
    public appTooltipText: InputSignal<string> = input.required();
    public appTooltipPosition: InputSignal<TooltipPositionEnum> = input.required();
    public appTooltipIsDisabled: InputSignal<boolean> = input(false);
    public appTooltipStyle: InputSignal<TooltipStyleEnum> = input<TooltipStyleEnum>(TooltipStyleEnum.DEFAULT);
    public appTooltipOffsetInPx: InputSignal<number> = input(16);
    public appTooltipDataQaTag: InputSignal<string | undefined> = input();
    public appTooltipAutoHideDelayMs: InputSignal<number | undefined> = input<number | undefined>();
    private readonly hostElement = inject(ElementRef);
    private tooltipElement!: HTMLElement;
    private readonly document = inject(DOCUMENT);
    private readonly window = inject(DOCUMENT).defaultView ?? window;
    private readonly VIEWPORT_PADDING_IN_PX = 16;
    private readonly IS_VISIBLE_CSS_CLASS = 'is-visible';
    private readonly IS_PRE_VISIBLE_CSS_CLASS = 'is-pre-visible';
    private longPressTimer: number | undefined;
    private autoHideTimer: number | undefined;

    // We need to use an effect, to update the tooltip-text and tooltip-style,
    // as long as we use the "old lists" and the status badges in the document
    // view that updates the states, like the tooltip text, by async API calls.
    private readonly updateTooltip: EffectRef = effect(() => {
        if (this.tooltipElement) {
            this.setTooltipText(this.tooltipElement, this.appTooltipText());
            this.setTooltipStyle(this.tooltipElement, this.appTooltipStyle());
        }
    });

    public ngOnInit(): void {
        if (this.appTooltipIsDisabled()) {
            return;
        }

        this.tooltipElement = this.createTooltipElement(this.appTooltipText(), this.appTooltipStyle(), this.appTooltipDataQaTag());

        const tooltipContainerElement = this.document.documentElement.querySelector('app-root');
        tooltipContainerElement?.classList.add('tooltip-container');
        tooltipContainerElement?.append(this.tooltipElement);
    }

    public ngOnDestroy(): void {
        this.tooltipElement?.remove();
        this.updateTooltip.destroy();
        this.clearLongPressTimer();
        this.clearAutoHideTimer();
        this.removeDocumentDismissListener();
    }

    protected showTooltip(): void {
        if (this.appTooltipIsDisabled() || !this.tooltipElement) {
            return;
        }

        this.tooltipElement.classList.add(this.IS_PRE_VISIBLE_CSS_CLASS);
        this.resetTooltipElementInlineStyles();

        const tooltipElementDimensions = this.tooltipElement.getBoundingClientRect();
        const hostElementDimensions = this.hostElement.nativeElement.getBoundingClientRect();

        switch (this.appTooltipPosition()) {
            case TooltipPositionEnum.TOP:
                this.placeTooltipAboveHostElement(tooltipElementDimensions, hostElementDimensions);
                break;
            case TooltipPositionEnum.RIGHT:
                this.placeTooltipBehindHostElement(tooltipElementDimensions, hostElementDimensions);
                break;
            case TooltipPositionEnum.BOTTOM:
                this.placeTooltipBelowHostElement(tooltipElementDimensions, hostElementDimensions);
                break;
            case TooltipPositionEnum.LEFT:
                this.placeTooltipInFrontOfHostElement(tooltipElementDimensions, hostElementDimensions);
                break;
        }

        this.preventViewportCollisions();

        this.tooltipElement.classList.add(this.IS_VISIBLE_CSS_CLASS);
    }

    protected hideTooltip(): void {
        if (this.appTooltipIsDisabled() || !this.tooltipElement) {
            return;
        }

        this.tooltipElement.classList.remove(this.IS_VISIBLE_CSS_CLASS, this.IS_PRE_VISIBLE_CSS_CLASS);
    }

    protected onPointerDown(event: PointerEvent): void {
        if (event.pointerType === 'mouse') {
            return;
        }

        this.longPressTimer = this.window.setTimeout(() => {
            this.longPressTimer = undefined;
            this.showTooltip();
            this.startAutoHideTimer();
            this.addDocumentDismissListener();
        }, defaultContextmenuDelay);
    }

    protected onPointerUpOrCancel(): void {
        this.clearLongPressTimer();
    }

    protected onPointerEnter(event: PointerEvent): void {
        if (event.pointerType === 'touch') {
            return;
        }

        this.showTooltip();
    }

    protected onPointerLeave(event: PointerEvent): void {
        this.clearLongPressTimer();

        if (event.pointerType === 'touch') {
            return;
        }

        this.hideTooltip();
    }

    private startAutoHideTimer(): void {
        const delay = this.appTooltipAutoHideDelayMs();
        if (delay === undefined) {
            return;
        }

        this.autoHideTimer = this.window.setTimeout(() => {
            this.hideTooltip();
            this.removeDocumentDismissListener();
            this.autoHideTimer = undefined;
        }, delay);
    }

    private addDocumentDismissListener(): void {
        this.document.addEventListener('pointerdown', this.onDocumentPointerDown, { once: true });
    }

    private removeDocumentDismissListener(): void {
        this.document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    private readonly onDocumentPointerDown = (): void => {
        this.hideTooltip();
        this.clearAutoHideTimer();
    };

    private clearLongPressTimer(): void {
        if (this.longPressTimer === undefined) {
            return;
        }

        this.window.clearTimeout(this.longPressTimer);
        this.longPressTimer = undefined;
    }

    private clearAutoHideTimer(): void {
        if (this.autoHideTimer === undefined) {
            return;
        }

        this.window.clearTimeout(this.autoHideTimer);
        this.autoHideTimer = undefined;
    }

    private preventViewportCollisions(): void {
        const tooltipElementDimensions = this.tooltipElement.getBoundingClientRect();

        if (tooltipElementDimensions.bottom > this.document.documentElement.clientHeight - this.VIEWPORT_PADDING_IN_PX) {
            // If the tooltip overlaps the bottom edge of the viewport, place the tooltip above the host element
            this.placeTooltipAboveHostElement(tooltipElementDimensions, this.hostElement.nativeElement.getBoundingClientRect());
        }

        if (tooltipElementDimensions.top < this.VIEWPORT_PADDING_IN_PX) {
            // If the tooltip overlaps the top edge of the viewport, show the tooltip below the host element
            this.placeTooltipBelowHostElement(tooltipElementDimensions, this.hostElement.nativeElement.getBoundingClientRect());
        }

        // We need to correct the position on the x-axis AFTER the tooltip possibly has been switched its placement from
        // "above" to "below", or the other way round, because the left or right property could have been changed.
        if (tooltipElementDimensions.right > this.document.documentElement.clientWidth - this.VIEWPORT_PADDING_IN_PX) {
            this.tooltipElement.style.removeProperty('left');
            this.tooltipElement.style.setProperty('right', this.VIEWPORT_PADDING_IN_PX + 'px');
        }

        if (tooltipElementDimensions.left < this.VIEWPORT_PADDING_IN_PX) {
            this.tooltipElement.style.removeProperty('right');
            this.tooltipElement.style.setProperty('left', this.VIEWPORT_PADDING_IN_PX + 'px');
        }
    }

    private createTooltipElement(tooltipText: string, tooltipStyle: TooltipStyleEnum, toolTipDataQaTag?: string): HTMLElement {
        const tooltipElement = document.createElement('span');

        if (toolTipDataQaTag) {
            tooltipElement.setAttribute('data-qa', toolTipDataQaTag);
        }

        tooltipElement.role = 'tooltip';
        this.setTooltipText(tooltipElement, tooltipText);
        this.setTooltipStyle(tooltipElement, tooltipStyle);

        return tooltipElement;
    }

    private setTooltipText(tooltipElement: HTMLElement, text: string): void {
        tooltipElement.innerText = text;
    }

    private setTooltipStyle(tooltipElement: HTMLElement, tooltipStyle: TooltipStyleEnum): void {
        let tooltipCssClass = '';

        switch (tooltipStyle) {
            case TooltipStyleEnum.SUCCESS:
                tooltipCssClass = 'success';
                break;
            case TooltipStyleEnum.DANGER:
                tooltipCssClass = 'danger';
                break;
            case TooltipStyleEnum.WARNING:
                tooltipCssClass = 'warning';
                break;
            case TooltipStyleEnum.INFO:
                tooltipCssClass = 'info';
                break;
            case TooltipStyleEnum.DEFAULT:
            default:
                tooltipCssClass = 'default';
                break;
        }

        tooltipElement.classList = '';
        tooltipElement.classList.add('tooltip', tooltipCssClass);
    }

    private resetTooltipElementInlineStyles(): void {
        [
            'top',
            'right',
            'bottom',
            'left'
        ].forEach((side) => {
            this.tooltipElement.style.removeProperty(side);
        });
    }

    private placeTooltipAboveHostElement(tooltipElementDimensions: DOMRect, hostElementDimensions: DOMRect): void {
        const leftDistanceInPx = hostElementDimensions.left + (hostElementDimensions.width / 2) - (tooltipElementDimensions.width / 2);
        const topDistanceInPx = hostElementDimensions.top - tooltipElementDimensions.height - this.appTooltipOffsetInPx();

        this.placeTooltip(topDistanceInPx, leftDistanceInPx);
    }

    private placeTooltipBehindHostElement(tooltipElementDimensions: DOMRect, hostElementDimensions: DOMRect): void {
        const leftDistanceInPx = hostElementDimensions.right + this.appTooltipOffsetInPx();
        const topDistanceInPx = hostElementDimensions.top - (hostElementDimensions.height / 2) - (tooltipElementDimensions.height / 2);

        this.placeTooltip(topDistanceInPx, leftDistanceInPx);
    }

    private placeTooltipBelowHostElement(tooltipElementDimensions: DOMRect, hostElementDimensions: DOMRect): void {
        const leftDistanceInPx = hostElementDimensions.left + (hostElementDimensions.width / 2) - (tooltipElementDimensions.width / 2);
        const topDistanceInPx = hostElementDimensions.bottom + this.appTooltipOffsetInPx();

        this.placeTooltip(topDistanceInPx, leftDistanceInPx);
    }

    private placeTooltipInFrontOfHostElement(tooltipElementDimensions: DOMRect, hostElementDimensions: DOMRect): void {
        const leftDistanceInPx = hostElementDimensions.left - tooltipElementDimensions.width - this.appTooltipOffsetInPx();
        const topDistanceInPx = hostElementDimensions.bottom - (hostElementDimensions.height / 2) - (tooltipElementDimensions.height / 2);

        this.placeTooltip(topDistanceInPx, leftDistanceInPx);
    }

    private placeTooltip(topDistanceInPx: number, leftDistanceInPx: number): void {
        this.tooltipElement.style.setProperty('top', topDistanceInPx + 'px');
        this.tooltipElement.style.setProperty('left', leftDistanceInPx + 'px');
    }
}
